import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import { useEstimate } from '@/hooks/useEstimate';
import { useEstimateStratigraphies } from '@/hooks/useEstimateStratigraphies';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { fetchFinishCostSnapshot } from '@/utils/estimates/fetchFinishCostSnapshot';
import EstimateHeader from '@/components/estimates/EstimateHeader';
import EstimateTimeline from '@/components/estimates/EstimateTimeline';
import EstimateStratiSummaryCards from '@/components/estimates/EstimateStratiSummaryCards';
import EstimateStratigraphiesSection from '@/components/estimates/EstimateStratigraphiesSection';
import MaterialsSummaryCard from '@/components/estimates/MaterialsSummaryCard';
import SellingPriceCard from '@/components/estimates/SellingPriceCard';
import EstimateStickySummaryBar from '@/components/estimates/EstimateStickySummaryBar';
import EstimateLoadingState from '@/components/estimates/EstimateLoadingState';
import EstimateErrorState from '@/components/estimates/EstimateErrorState';
import { finishedFacesFor } from '@/components/configurator-v2/hooks/useTypologyDetection';

const EstimateManagement = () => {
  const { estimateId } = useParams();
  
  const { estimate, isLoading: isLoadingEstimate } = useEstimate(estimateId || '');
  const { 
    estimateStratigraphies, 
    isLoading: isLoadingStratigraphies,
    totalCost,
    createEstimateStratigraphy, 
    updateEstimateStratigraphy, 
    deleteEstimateStratigraphy,
    updateStratigraphyPrices,
    isCreating,
    isUpdating,
    isDeleting,
    isUpdatingPrices
  } = useEstimateStratigraphies(estimateId);
  const { currentOrganizationId } = useCurrentOrganization();

  // Cambio livello finitura inline: ricalcola costo finitura (materiali netti +
  // manodopera) + snapshot BOM, deriva il nuovo unit_cost = base + finitura e
  // salva. finishCode null = "Nessuna finitura" (azzera il costo finitura).
  const handleUpdateFinishLevel = async (id: string, finishCode: string | null) => {
    const item = estimateStratigraphies.find((s) => s.id === id);
    if (!item) return;
    let newFinishCost = 0;
    let newFinishLaborMin: number | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let newFinishComponents: any[] | null = null;
    if (finishCode && currentOrganizationId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const finishedFaces = finishedFacesFor((item as any).stratigraphy?.layers);
      const snap = await fetchFinishCostSnapshot(currentOrganizationId, finishCode, finishedFaces);
      if (snap) {
        newFinishCost = snap.finishCostPerSqm;
        newFinishLaborMin = snap.finishLaborMinPerSqm;
        newFinishComponents = snap.finishComponentsSnapshot;
      }
    }
    // base = unit_cost attuale meno il vecchio costo finitura snapshottato.
    const oldFinishCost = item.finishCostPerSqm ?? 0;
    const baseCost = Math.max(0, item.unitCost - oldFinishCost);
    const newUnitCost = Math.round((baseCost + newFinishCost) * 100) / 100;
    updateEstimateStratigraphy(id, {
      finishLevel: finishCode,
      finishCostPerSqm: newFinishCost > 0 ? newFinishCost : null,
      finishLaborMinutesPerSqm: newFinishLaborMin,
      finishComponentsData: newFinishComponents,
      unitCost: newUnitCost,
    });
  };

  if (!estimateId) {
    return <div>ID preventivo non trovato</div>;
  }

  if (isLoadingEstimate || isLoadingStratigraphies) {
    return <EstimateLoadingState />;
  }

  if (!estimate) {
    return <EstimateErrorState />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <SidebarInset className="flex flex-col">
          <Header />
          <main className="flex-1 p-6 md:p-8 space-y-6">
            <EstimateHeader estimate={estimate} stratigraphies={estimateStratigraphies} />

            <EstimateTimeline estimate={estimate} />

            <EstimateStratiSummaryCards 
              estimate={estimate} 
              stratigraphies={estimateStratigraphies} 
              totalCost={totalCost} 
            />

            <EstimateStratigraphiesSection
              stratigraphies={estimateStratigraphies}
              estimateId={estimateId}
              estimateStatus={estimate.status}
              onUpdateStratigraphy={updateEstimateStratigraphy}
              onUpdateFinishLevel={handleUpdateFinishLevel}
              onDeleteStratigraphy={deleteEstimateStratigraphy}
              onUpdatePrices={updateStratigraphyPrices}
              isUpdating={isUpdating}
              isDeleting={isDeleting}
              isUpdatingPrices={isUpdatingPrices}
              pricingOverrides={(estimate as { pricingOverrides?: any }).pricingOverrides ?? null}
            />

            <SellingPriceCard stratigraphies={estimateStratigraphies} estimate={estimate} />

            <MaterialsSummaryCard
              stratigraphies={estimateStratigraphies}
              estimateName={estimate.name}
              estimate={estimate}
            />

            <EstimateStickySummaryBar
              stratigraphies={estimateStratigraphies}
              totalCost={totalCost}
              pricingOverrides={(estimate as { pricingOverrides?: any }).pricingOverrides ?? null}
            />
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default EstimateManagement;
